import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PiPlus } from 'react-icons/pi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ShareLinkModal from '../components/ShareLinkModal';
import PullToRefresh from '../components/PullToRefresh';
import CalculatorModal from '../components/CalculatorModal';
import PeriodNavigator from '../components/PeriodNavigator';
import PeriodContentTransition from '../components/PeriodContentTransition';
import DashboardHeader from '../components/DashboardHeader';
import HomeDashboard from '../components/HomeDashboard';
import AddItemModal from '../components/AddItemModal';
import NaraAssistantLauncher from '../components/NaraAssistantLauncher';
import { createAccountInviteLink } from '../services/accountService';
import { createRecurringTransactionFromDraft } from '../services/recurringTransactionService';
import { remoteOperationMessage } from '../services/connectivityService';
import { compareYearMonthKeys, formatYearMonthLabel, fromYearMonthParam, getCurrentYearMonthKey, parseYearMonthKey, shiftYearMonthKey, toYearMonthParam } from '../utils/period';
import { TRANSACTION_TYPES, calculateBudgetUsage, calculateFinancialSummary, getTodayCivilDate, getUpcomingFinancialRange } from '../domain/finance';
import { useFeedback } from '../FeedbackContext';
import { useFinancialFeedback } from '../hooks/useFinancialFeedback';
import { useTransactions } from '../hooks/useTransactions';
import { useFinancialOccurrences } from '../hooks/useFinancialOccurrences';
import { useBudget } from '../hooks/useBudget';
import { useCategories } from '../hooks/useCategories';
import './Dashboard.css';

const NaraAssistant = lazy(() => import('../components/NaraAssistant'));

const Dashboard = ({
  currentAccountId,
  currentRole,
  currentAccount,
  currentUser,
  onOpenAccountSwitcher,
}) => {
  const navigate = useNavigate();
  const currentPeriod = getCurrentYearMonthKey();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedPeriod, setSelectedPeriod] = useState(
    () => fromYearMonthParam(searchParams.get('period')) || currentPeriod
  );
  const [periodDirection, setPeriodDirection] = useState('forward');
  const [showShare, setShowShare] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [shareGenerating, setShareGenerating] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addModalInitialType, setAddModalInitialType] = useState(TRANSACTION_TYPES.INCOME);
  const [addModalInitialDraft, setAddModalInitialDraft] = useState(null);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantMounted, setAssistantMounted] = useState(false);
  const [assistantDraft, setAssistantDraft] = useState('');
  const naraEditSavedRef = useRef(null);
  const { showError } = useFeedback();
  const { showSuccess, showError: showFinancialError } = useFinancialFeedback();

  const {
    receitas,
    despesas,
    transactions,
    loading,
    loadingMore,
    hasMore,
    error: transactionsError,
    resolvedPeriod,
    retry,
    loadMore,
    addTransaction,
    toggleTransactionStatus,
  } = useTransactions(currentAccountId, selectedPeriod, { complete: true });
  const previousPeriod = useMemo(() => shiftYearMonthKey(selectedPeriod, -1), [selectedPeriod]);
  const {
    receitas: previousReceitas,
    despesas: previousDespesas,
    isResolved: previousIsResolved,
    resolvedAccountId: previousResolvedAccountId,
    resolvedPeriod: previousResolvedPeriod,
  } = useTransactions(currentAccountId, previousPeriod, { complete: true });
  const recentEndDate = useMemo(() => getTodayCivilDate(), []);
  const {
    transactions: recentTransactions,
    loading: recentLoading,
    error: recentError,
    isResolved: recentIsResolved,
    resolvedAccountId: recentResolvedAccountId,
    retry: retryRecent,
  } = useTransactions(currentAccountId, null, { endDate: recentEndDate });
  const { categories, categoryNames } = useCategories(currentAccountId);
  const {
    budget,
    loading: budgetLoading,
    error: budgetError,
    resolvedPeriod: budgetResolvedPeriod,
  } = useBudget(currentAccountId, selectedPeriod, categories);

  const upcomingRange = useMemo(() => getUpcomingFinancialRange(), []);
  const {
    occurrences: upcomingOccurrences,
    loading: upcomingLoading,
    error: upcomingError,
    resolvedRange: upcomingResolvedRange,
    resolvedAccountId: upcomingResolvedAccountId,
    retry: retryUpcoming,
  } = useFinancialOccurrences({ accountId: currentAccountId, ...upcomingRange });

  const publishedUpcomingOccurrences = upcomingResolvedAccountId === currentAccountId
    && upcomingResolvedRange?.startDate === upcomingRange.startDate
    && upcomingResolvedRange?.endDate === upcomingRange.endDate
    ? upcomingOccurrences
    : [];
  const publishedRecentTransactions = recentIsResolved && recentResolvedAccountId === currentAccountId
    ? recentTransactions
    : [];
  const financialSummary = useMemo(
    () => calculateFinancialSummary({ receitas, despesas }),
    [receitas, despesas]
  );
  const previousFinancialSummary = useMemo(
    () => previousIsResolved
      && previousResolvedAccountId === currentAccountId
      && previousResolvedPeriod === previousPeriod
      ? calculateFinancialSummary({ receitas: previousReceitas, despesas: previousDespesas })
      : null,
    [
      currentAccountId,
      previousDespesas,
      previousIsResolved,
      previousPeriod,
      previousReceitas,
      previousResolvedAccountId,
      previousResolvedPeriod,
    ]
  );
  const budgetUsage = useMemo(
    () => calculateBudgetUsage({ transactions, budget, validCategories: categoryNames }),
    [budget, categoryNames, transactions]
  );
  const showBudgetSummary = Boolean(parseYearMonthKey(selectedPeriod));
  const homeLoading = loading || (showBudgetSummary && budgetLoading);
  const homeResolvedPeriod = resolvedPeriod === selectedPeriod
    && (!showBudgetSummary || budgetResolvedPeriod === selectedPeriod)
    ? selectedPeriod
    : '';

  useEffect(() => {
    if (budget && hasMore && !loadingMore) loadMore().catch(() => {});
  }, [budget, hasMore, loadMore, loadingMore, transactions.length]);

  const handlePeriodChange = (period) => {
    setPeriodDirection(compareYearMonthKeys(period, selectedPeriod) < 0 ? 'backward' : 'forward');
    setSelectedPeriod(period);
    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams);
      nextParams.set('period', toYearMonthParam(period));
      return nextParams;
    }, { replace: true });
  };

  const handleOpenAddModal = (type, initialDraft = null) => {
    const preferredType = type || localStorage.getItem('lastUsedType');
    setAddModalInitialType(
      preferredType === TRANSACTION_TYPES.EXPENSE
        ? TRANSACTION_TYPES.EXPENSE
        : TRANSACTION_TYPES.INCOME
    );
    setAddModalInitialDraft(initialDraft);
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setAddModalInitialDraft(null);
    naraEditSavedRef.current = null;
  };

  const openAssistant = useCallback((draft = '') => {
    setAssistantDraft(draft);
    setAssistantMounted(true);
    setAssistantOpen(true);
  }, []);

  const editAssistantProposal = useCallback((proposal, onSaved) => {
    naraEditSavedRef.current = onSaved;
    handleOpenAddModal(proposal.type === 'income' ? TRANSACTION_TYPES.INCOME : TRANSACTION_TYPES.EXPENSE, {
      nome: proposal.description,
      amountCents: proposal.amountCents,
      date: proposal.date,
      categoria: proposal.categoryName,
      tipo: proposal.type === 'income' ? TRANSACTION_TYPES.INCOME : TRANSACTION_TYPES.EXPENSE,
      status: proposal.status === 'received' ? 'Recebido' : proposal.status === 'paid' ? 'Pago' : 'Pendente',
    });
  }, []);
  const editAssistantTransaction = useCallback((transaction) => {
    setAssistantOpen(false);
    navigate(`/movimentacoes?edit=${encodeURIComponent(transaction.id)}`);
  }, [navigate]);

  const handleOpenVirtualOccurrence = (item) => {
    const params = new URLSearchParams({ view: 'future', edit: item.occurrenceKey || item.id });
    navigate(`/movimentacoes?${params.toString()}`);
  };

  const handleAdd = async (newItem) => {
    try {
      await addTransaction(newItem);
      naraEditSavedRef.current?.();
      naraEditSavedRef.current = null;
      showSuccess(
        newItem.tipo === TRANSACTION_TYPES.INCOME
          ? 'A receita foi adicionada com sucesso.'
          : 'A despesa foi adicionada com sucesso.',
        'Movimentação adicionada'
      );
    } catch (error) {
      console.error('Erro ao adicionar movimentação:', error);
      showFinancialError(remoteOperationMessage(error, 'Não foi possível adicionar a movimentação. Tente novamente.'));
      throw error;
    }
  };

  const handleAddRecurring = async ({ transaction, recurrence }) => {
    try {
      await createRecurringTransactionFromDraft({
        accountId: currentAccountId,
        actorUid: currentUser.uid,
        transaction,
        recurrence,
      });
      retryUpcoming();
      showSuccess('Recorrência criada com sucesso.', 'Movimentação recorrente criada');
    } catch (error) {
      console.error('Erro ao criar recorrência:', error);
      showFinancialError(remoteOperationMessage(error, 'Não foi possível criar a recorrência. Tente novamente.'));
      throw error;
    }
  };

  const handleOpenShare = () => {
    if (!['owner', 'admin'].includes(currentRole)) {
      showError('Somente titular ou administrador pode criar convites para esta conta.');
      return;
    }
    setShareLink('');
    setShowShare(true);
  };

  const handleGenerateLink = async (role) => {
    setShareGenerating(true);
    try {
      const link = await createAccountInviteLink({
        accountId: currentAccountId,
        createdBy: currentUser.uid,
        role,
      });
      setShareLink(link);
    } catch (error) {
      console.error('Erro ao gerar link de compartilhamento:', error);
      showError(remoteOperationMessage(error, 'Não foi possível criar o convite. Confirme sua permissão e tente novamente.'));
      throw error;
    } finally {
      setShareGenerating(false);
    }
  };

  const periodName = formatYearMonthLabel(selectedPeriod, { includeYear: false })?.toLocaleLowerCase('pt-BR');
  const loadingLabel = periodName ? 'Atualizando ' + periodName + '...' : 'Atualizando período...';

  return (
    <PullToRefresh onRefresh={() => Promise.resolve()}>
      <div className="nf-dashboard-container">
        <DashboardHeader
          currentUser={currentUser}
          currentAccount={currentAccount}
          onOpenAccountSwitcher={onOpenAccountSwitcher}
          onNew={() => handleOpenAddModal()}
          onCalculator={() => setShowCalculator(true)}
          onShare={handleOpenShare}
        />

        <NaraAssistantLauncher onOpen={openAssistant} />

        <PeriodNavigator
          value={selectedPeriod}
          currentPeriod={currentPeriod}
          onChange={handlePeriodChange}
        />

        {transactionsError && !homeLoading ? (
          <section className="nf-data-error" role="alert">
            <strong>Não foi possível carregar seu painel financeiro.</strong>
            <span>Verifique a conexão e tente novamente.</span>
            <button type="button" onClick={retry}>Tentar novamente</button>
          </section>
        ) : (
          <PeriodContentTransition
            loading={homeLoading}
            period={selectedPeriod}
            resolvedPeriod={homeResolvedPeriod}
            direction={periodDirection}
            label={loadingLabel}
          >
            <HomeDashboard
              summary={financialSummary}
              previousSummary={previousFinancialSummary}
              transactions={transactions}
              upcomingTransactions={publishedUpcomingOccurrences}
              recentTransactions={publishedRecentTransactions}
              upcomingLoading={upcomingLoading}
              upcomingError={upcomingError}
              onRetryUpcoming={retryUpcoming}
              recentLoading={recentLoading}
              recentError={recentError}
              onRetryRecent={retryRecent}
              onOpenVirtualOccurrence={handleOpenVirtualOccurrence}
              selectedPeriod={selectedPeriod}
              showBudgetSummary={showBudgetSummary}
              budget={budget}
              budgetUsage={budgetUsage}
              budgetLoading={Boolean(budget && (loadingMore || hasMore))}
              budgetError={budgetError}
            />
          </PeriodContentTransition>
        )}

        {showShare && (
          <ShareLinkModal
            shareLink={shareLink}
            isGenerating={shareGenerating}
            onGenerate={handleGenerateLink}
            onClose={() => setShowShare(false)}
          />
        )}

        {showCalculator && <CalculatorModal onClose={() => setShowCalculator(false)} />}

        {showAddModal && (
          <AddItemModal
            accountId={currentAccountId}
            currentRole={currentRole}
            show
            initialType={addModalInitialType}
            initialDraft={addModalInitialDraft}
            selectedPeriod={selectedPeriod}
            onClose={closeAddModal}
            onSave={handleAdd}
            onSaveRecurring={handleAddRecurring}
          />
        )}

        {assistantMounted && <Suspense fallback={null}>
          <NaraAssistant
            open={assistantOpen}
            initialDraft={assistantDraft}
            onDraftConsumed={() => setAssistantDraft('')}
            onClose={() => setAssistantOpen(false)}
            user={currentUser}
            accountId={currentAccountId}
            currentRole={currentRole}
            accountName={currentAccount?.name || currentAccount?.accountName}
            categories={categories}
            currentPeriod={currentPeriod}
            selectedPeriod={selectedPeriod}
            dashboardTransactions={transactions}
            upcomingTransactions={publishedUpcomingOccurrences}
            dashboardLoading={loading}
            dashboardError={transactionsError}
            onAddTransaction={handleAdd}
            onEditTransaction={editAssistantProposal}
            onEditExistingTransaction={editAssistantTransaction}
            onViewAllTransactions={(status) => navigate(`/movimentacoes?period=${encodeURIComponent(toYearMonthParam(currentPeriod))}${status ? `&status=${encodeURIComponent(status)}` : ''}`)}
            onChangeTransactionStatus={toggleTransactionStatus}
          />
        </Suspense>}

        <button
          type="button"
          className="dashboard-v2-fab"
          onClick={() => handleOpenAddModal()}
          aria-label="Nova movimentação"
        >
          <PiPlus aria-hidden="true" />
        </button>
      </div>
    </PullToRefresh>
  );
};

export default Dashboard;
